import React, { useState } from 'react';
import classNames from 'classnames';

import Link from 'src/components/Link/Link';
import { observer } from 'src/utils/mobx-react';
import { DateTime } from 'src_common/utils/time/time';
import { ChatRequestNotificationType, ChatResponseSchemaType } from 'src_server/types/notificationData';
import { makeAutoObservable, action, observable, computed } from 'mobx';
import { Resource } from 'src_common/common/mobx-utils/Resource';

import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';

const loadingAnimation = keyframes`
  0% {
    width: 0%;
  }
  100% {
    width: 100%;
  }
`;

const LoaderWrapper = styled.div`
    width: 100%;
    height: 12px;
    background-color: rgba(244, 244, 244, 0.6);
    overflow: hidden;
`;

const LoadingBar = styled.div`
    height: 100%;
    background-color: rgba(224, 224, 224, 0.2);
    animation: ${loadingAnimation} 2s linear infinite;
`;

const Loader = (): JSX.Element => {
    return (
        <LoaderWrapper>
            <LoadingBar />
        </LoaderWrapper>
    );
};

const observerOptions: IntersectionObserverInit = {
    root: null,
    rootMargin: '100px',
    threshold: 0.15,
};

class ChatRequestNotificationState {
    private readonly observer: IntersectionObserver | null = null;
    public readonly lastMsgResource: Resource<ChatResponseSchemaType>;
    @observable public msgRef: HTMLImageElement | null = null;
    @observable public allowLoad: boolean = false;

    public constructor(public readonly chatId: string) {
        this.observer = new IntersectionObserver(this.loadLastMsg, observerOptions);
        this.lastMsgResource = new Resource(async (): Promise<ChatResponseSchemaType> => {
            return await $appState.trpcClient.client.notification.getChatLastMessage.query({ chatId: this.chatId });
        });
        makeAutoObservable(this);
    }

    public setMsgRef = (ref: HTMLImageElement | null): void => {
        this.msgRef = ref;
    };

    public observe = (): void => {
        if (this.msgRef !== null && this.observer !== null) {
            this.observer.observe(this.msgRef);
        }
    };

    public unobserve = (): void => {
        if (this.observer !== null) {
            this.observer.disconnect();
        }
    };

    @action private loadLastMsg = (entries: IntersectionObserverEntry[]): void => {
        entries.forEach((entry) => {
            if (entry.intersectionRatio > 0) {
                this.allowLoad = true;
                this.observer?.unobserve(entry.target);
            }
        });
    };

    @computed public get lastMessage(): string {
        if (this.allowLoad) {
            const msg = this.lastMsgResource.get();
            if (msg.type === 'ready') {
                return msg.value.content?.text ?? '';
            }

            if (msg.type === 'loading') {
                return 'loading';
            }
        }

        return '';
    }
}

type PropsType = {
    chatItem: ChatRequestNotificationType;
    onClose: () => void;
};

export const ChatRequestNotification = observer('ChatRequestNotification', (props: PropsType) => {
    const { onClose } = props;
    const { status, createdAt, details } = props.chatItem;
    const [state] = useState(() => new ChatRequestNotificationState(props.chatItem.details.chatId));

    React.useEffect(() => {
        state.observe();
        return (): void => state.unobserve();
    }, []);

    const age = DateTime.from(createdAt)?.fromNow();

    const sender = details.participants.find((p) => p.role === 'owner');

    const chatId = details.chatId;
    const senderName = sender?.account.name;

    const classNamesList = classNames([
        'chat-request-notification',
        'notification',
        {
            pending: status === 'pending',
            ongoing: status === 'ongoing',
        },
    ]);

    return (
        <div
            className={classNamesList}
            ref={state.setMsgRef}
        >
            <div className='type'>Chat Waiting</div>
            <div className='sender'>{senderName}</div>

            {state.lastMessage === 'loading' ? <Loader /> : <div className='last-message'>{state.lastMessage}</div>}

            <div>
                <div className='time'>{age}</div>
                <div className='buttons'>
                    <Link
                        route={`customers:${chatId}`}
                        parameters={{
                            'tab.tab': 'customer-summary',
                            chat: 'chat',
                        }}
                        onClick={onClose}
                        className='btn'
                    >
                        Chat
                    </Link>
                </div>
            </div>
        </div>
    );
});
